import {
  currentMatchResponseSchema,
  type LobbyVisibility,
  type MatchView,
  type MaxPlayers,
  type SubmitChoiceRequest,
  type SubmitVoteRequest,
} from '@soe/protocol';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';
import { useSession } from '../auth/SessionProvider.js';
import { RealtimeConnection, type ConnectionStatus } from '../realtime/connection.js';

interface MatchState {
  match: MatchView | null;
  /** serverTime - clientTime in ms, so countdowns follow the server's deadlines. */
  clockOffsetMs: number;
  status: ConnectionStatus | 'idle';
  loaded: boolean;
  /** Set when the first load failed and nothing has arrived since; cleared by any update. */
  loadError: string | null;
}

interface MatchActions {
  createLobby: (options: { visibility: LobbyVisibility; maxPlayers: MaxPlayers }) => Promise<void>;
  joinLobby: (code: string) => Promise<void>;
  /** Public matchmaking: no code, the server finds or opens a public lobby. */
  findPublicMatch: () => Promise<void>;
  setLobbySize: (maxPlayers: MaxPlayers) => Promise<void>;
  choose: (input: SubmitChoiceRequest) => Promise<{ choiceId: string }>;
  leave: () => Promise<void>;
  setReady: (ready: boolean) => Promise<void>;
  vote: (input: SubmitVoteRequest) => Promise<{ countryId: string }>;
  /** Starts over: reloads the match over HTTP and reopens the live connection. */
  retry: () => void;
}

const MatchContext = createContext<(MatchState & MatchActions) | null>(null);

function offsetFrom(match: MatchView | null, receivedAt: number): number | null {
  return match ? Date.parse(match.serverNow) - receivedAt : null;
}

/**
 * Mirrors the server's match view. The server pushes every change over the realtime
 * channel; HTTP responses are applied too, so the UI updates even if a push is late.
 */
export function MatchProvider({ children }: { children: ReactNode }) {
  const { state: session, refresh } = useSession();
  // Keyed on the guest, not just on having one: after "Forget this browser" a new guest
  // replaces the old one with no gap, and must start from a clean slate and a new connection.
  const guestId = session.status === 'ready' ? session.session.guest.id : null;
  const [state, setState] = useState<MatchState>({
    match: null,
    clockOffsetMs: 0,
    status: 'idle',
    loaded: false,
    loadError: null,
  });
  const [attempt, setAttempt] = useState(0);

  const apply = (match: MatchView | null, receivedAt = Date.now()) => {
    setState((prev) => ({
      ...prev,
      match,
      loaded: true,
      loadError: null,
      clockOffsetMs: offsetFrom(match, receivedAt) ?? prev.clockOffsetMs,
    }));
  };

  useEffect(() => {
    setState({ match: null, clockOffsetMs: 0, status: 'idle', loaded: false, loadError: null });
    if (!guestId) return;
    const connection = new RealtimeConnection({
      onStatus: (status) => {
        setState((prev) => ({ ...prev, status }));
        if (status === 'session-ended') void refresh();
      },
      onMatch: (match, receivedAt) => {
        apply(match, receivedAt);
      },
    });
    connection.start();
    // Initial state over HTTP too, so the screen fills even before the socket opens.
    const controller = new AbortController();
    api('GET', '/matches/current', undefined, { signal: controller.signal })
      .then((raw) => {
        apply(currentMatchResponseSchema.parse(raw).match);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // Shown only if the live connection has not delivered the match in the meantime.
        setState((prev) =>
          prev.loaded
            ? prev
            : {
                ...prev,
                loadError:
                  err instanceof ApiError
                    ? err.message
                    : 'The server could not be reached. Check your connection.',
              },
        );
      });
    return () => {
      controller.abort();
      connection.stop();
    };
  }, [guestId, refresh, attempt]);

  const actions = useMemo<MatchActions>(() => {
    const matchId = () => {
      if (!state.match) throw new Error('not in a match');
      return state.match.id;
    };
    const adopt = (raw: unknown) => {
      apply(currentMatchResponseSchema.parse(raw).match);
    };
    return {
      createLobby: async (options) => {
        adopt(await api('POST', '/matches', options));
      },
      joinLobby: async (code: string) => {
        adopt(await api('POST', '/matches/join', { code }));
      },
      findPublicMatch: async () => {
        adopt(await api('POST', '/matches/quick-join', {}));
      },
      choose: async (input) =>
        api<{ choiceId: string }>('POST', `/matches/${matchId()}/choices`, input),
      setLobbySize: async (maxPlayers) => {
        adopt(await api('POST', `/matches/${matchId()}/size`, { maxPlayers }));
      },
      leave: async () => {
        await api('POST', `/matches/${matchId()}/leave`, {});
        apply(null);
      },
      setReady: async (ready: boolean) => {
        adopt(await api('POST', `/matches/${matchId()}/ready`, { ready }));
      },
      vote: async (input: SubmitVoteRequest) =>
        api<{ countryId: string }>('POST', `/matches/${matchId()}/votes`, input),
      retry: () => {
        setState((prev) => ({ ...prev, loadError: null }));
        setAttempt((n) => n + 1);
      },
    };
  }, [state.match]);

  const value = useMemo(() => ({ ...state, ...actions }), [state, actions]);
  return <MatchContext.Provider value={value}>{children}</MatchContext.Provider>;
}

export function useMatch() {
  const ctx = useContext(MatchContext);
  if (!ctx) throw new Error('useMatch must be used inside MatchProvider');
  return ctx;
}

/** Seconds left until an ISO deadline, by the server's clock. Updates 4× a second. */
export function useCountdown(deadline: string | null, clockOffsetMs: number): number | null {
  const compute = () =>
    deadline === null
      ? null
      : Math.max(0, Math.ceil((Date.parse(deadline) - (Date.now() + clockOffsetMs)) / 1000));
  const [seconds, setSeconds] = useState(compute);
  useEffect(() => {
    setSeconds(compute());
    if (deadline === null) return;
    const id = window.setInterval(() => {
      setSeconds(compute());
    }, 250);
    return () => {
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- compute derives from these two
  }, [deadline, clockOffsetMs]);
  return seconds;
}
